import { SCORE_CONFIG } from "../scoreConfig";
import type { ScoreRecord, ScoreResult } from "../types";

const sanitizeAlias = (value: string) => {
  const cleaned = value
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, SCORE_CONFIG.aliasMaxLength);
  return cleaned || SCORE_CONFIG.defaultName;
};

const generateId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to manual UUID below (older mobile webviews).
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

// Normalizes any stored record (including legacy v3 with `name` and no
// deviceId/schemaVersion) into the current shape without losing data.
const normalizeRecord = (raw: Partial<ScoreRecord> | undefined, deviceId: string): ScoreRecord | null => {
  if (!raw || !Number.isFinite(raw.score)) return null;
  const aliasSource = typeof raw.alias === "string" ? raw.alias : typeof raw.name === "string" ? raw.name : "";
  return {
    schemaVersion: typeof raw.schemaVersion === "number" ? raw.schemaVersion : SCORE_CONFIG.schemaVersion,
    id: typeof raw.id === "string" ? raw.id : generateId(),
    alias: sanitizeAlias(aliasSource),
    deviceId: typeof raw.deviceId === "string" ? raw.deviceId : deviceId,
    score: raw.score as number,
    elapsed: Number.isFinite(raw.elapsed) ? (raw.elapsed as number) : 0,
    distance: Number.isFinite(raw.distance) ? (raw.distance as number) : 0,
    victory: Boolean(raw.victory),
    rankId: raw.rankId,
    rankName: raw.rankName,
    collectedIsotypePercent: raw.collectedIsotypePercent,
    beatSyncPercent: raw.beatSyncPercent,
    bestCombo: raw.bestCombo,
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : new Date().toISOString(),
    synced: raw.synced,
    remoteId: raw.remoteId
  };
};

const byScoreDesc = (a: ScoreRecord, b: ScoreRecord) => b.score - a.score || b.elapsed - a.elapsed;

// One row per alias, keeping each player's best run (tiebreak on elapsed).
const aggregateByAlias = (records: ScoreRecord[]): ScoreRecord[] => {
  const best = new Map<string, ScoreRecord>();
  for (const record of records) {
    const current = best.get(record.alias);
    if (!current || record.score > current.score || (record.score === current.score && record.elapsed > current.elapsed)) {
      best.set(record.alias, record);
    }
  }
  return [...best.values()].sort(byScoreDesc);
};

export interface SaveOutcome {
  accepted: boolean;
  record?: ScoreRecord;
  previous?: ScoreRecord;
  records: ScoreRecord[];
  rank: number;
}

export class ScoreRecords {
  // Stable per-device id, created once. Inert in Stage 1; reserved for Stage 2
  // server-side dedup/anti-abuse. Not used to group players locally.
  static deviceId(): string {
    try {
      const existing = window.localStorage.getItem(SCORE_CONFIG.deviceIdKey);
      if (existing) return existing;
      const id = generateId();
      window.localStorage.setItem(SCORE_CONFIG.deviceIdKey, id);
      return id;
    } catch {
      return "unknown-device";
    }
  }

  // Full best-per-alias set (no slice) — used internally for personal-best gating.
  private static loadAggregated(): ScoreRecord[] {
    try {
      const raw = window.localStorage.getItem(SCORE_CONFIG.storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Array<Partial<ScoreRecord>>;
      if (!Array.isArray(parsed)) return [];
      const deviceId = this.deviceId();
      const normalized = parsed
        .map((item) => normalizeRecord(item, deviceId))
        .filter((item): item is ScoreRecord => item !== null);
      return aggregateByAlias(normalized);
    } catch {
      return [];
    }
  }

  // Visible leaderboard: best-per-alias, top N.
  static load(): ScoreRecord[] {
    return this.loadAggregated().slice(0, SCORE_CONFIG.leaderboardSize);
  }

  // Would this score earn a top-N spot for this alias? (Label preview only.)
  static canEnterTop(score: number, aliasInput: string) {
    const alias = sanitizeAlias(aliasInput);
    const aggregated = this.loadAggregated();
    const previous = aggregated.find((record) => record.alias === alias);
    if (previous && score <= previous.score) return false;
    const betterCount = aggregated.filter((record) => record.alias !== alias && record.score > score).length;
    return betterCount < SCORE_CONFIG.leaderboardSize;
  }

  static personalBest(aliasInput: string): ScoreRecord | undefined {
    const alias = sanitizeAlias(aliasInput);
    return this.loadAggregated().find((record) => record.alias === alias);
  }

  static save(aliasInput: string, result: ScoreResult): SaveOutcome {
    const alias = sanitizeAlias(aliasInput);
    const aggregated = this.loadAggregated();
    const previous = aggregated.find((record) => record.alias === alias);
    const score = result.total;

    // Personal-best gate: accept only for a new alias or a strictly better run.
    const beatsPrevious =
      !previous || score > previous.score || (score === previous.score && result.elapsed > previous.elapsed);

    if (!beatsPrevious) {
      const records = aggregated.slice(0, SCORE_CONFIG.leaderboardSize);
      return {
        accepted: false,
        previous,
        records,
        rank: records.findIndex((record) => record.alias === alias) + 1
      };
    }

    const record: ScoreRecord = {
      schemaVersion: SCORE_CONFIG.schemaVersion,
      id: generateId(),
      alias,
      deviceId: this.deviceId(),
      score,
      elapsed: result.elapsed,
      distance: result.distance,
      victory: result.victory,
      rankId: result.rankId,
      rankName: result.rankName,
      collectedIsotypePercent: result.collectedIsotypePercent,
      beatSyncPercent: result.beatSyncPercent,
      bestCombo: result.bestCombo,
      createdAt: new Date().toISOString()
    };

    // Replace this alias's entry, keep every other player's best.
    const merged = aggregated.filter((item) => item.alias !== alias).concat(record).sort(byScoreDesc);
    // Persist the full best-per-alias set so players outside the visible top N
    // still keep their personal best for future comparisons.
    window.localStorage.setItem(SCORE_CONFIG.storageKey, JSON.stringify(merged));
    window.localStorage.setItem(`${SCORE_CONFIG.storageKey}:lastName`, record.alias);

    const records = merged.slice(0, SCORE_CONFIG.leaderboardSize);
    return {
      accepted: true,
      record,
      previous,
      records,
      rank: records.findIndex((item) => item.id === record.id) + 1
    };
  }

  static lastName() {
    return window.localStorage.getItem(`${SCORE_CONFIG.storageKey}:lastName`) || SCORE_CONFIG.defaultName;
  }

  // Raw stored alias (null when the player has never saved). Unlike lastName(),
  // it does NOT fall back to defaultName, so the UI can show a placeholder for
  // first-time players and pre-fill the field for returning ones.
  static storedName(): string | null {
    try {
      return window.localStorage.getItem(`${SCORE_CONFIG.storageKey}:lastName`);
    } catch {
      return null;
    }
  }

  // Auto-generated alias for players who save with an empty field, e.g. "LOBO4821".
  // Routed through sanitizeAlias so it always satisfies the existing validation
  // (A-Z 0-9, max length). Four digits keep it well under aliasMaxLength.
  static randomAlias(): string {
    const suffix = 1000 + Math.floor(Math.random() * 9000);
    return sanitizeAlias(`${SCORE_CONFIG.defaultName}${suffix}`);
  }
}
